import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

export default function AgentAcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [status, setStatus] = useState('validating'); // validating | valid | invalid
  const [invitee, setInvitee] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    fetch(`${API_BASE}/auth/agent/invite/${token}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.success) {
          setInvitee(json.data);
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/agent/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Something went wrong');
      localStorage.setItem('token', json.data.token);
      navigate('/agent/applications');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 font-['Plus_Jakarta_Sans']"
      style={{ background: 'radial-gradient(circle at top right, #1e3c72, #00255a 60%, #001a42 100%)' }}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-6">
            {/* <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-lg">
              <span className="material-symbols-outlined text-[#00255a] text-3xl">domain</span>
            </div> */}
            <a href="/" className='inline-flex items-center gap-3 '>
            <img src="/images/logo_w_nobg.png" alt="IASC Consultancy" className="h-10 w-10 object-contain rounded-lg bg-white" />
            <h1 className="text-2xl text-white tracking-tight font-semibold">IASC Consultancy</h1>
            </a>
          </div>
          <div className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
            <span className="text-xs text-[#aec6ff] uppercase tracking-widest flex items-center gap-2">
              <span className="material-symbols-outlined text-sm">badge</span>
              Agent Account Setup
            </span>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-[0px_20px_60px_rgba(0,0,0,0.3)] p-10">
          {status === 'validating' && (
            <div className="text-center py-8">
              <p className="text-[#64748b]">Validating your invite link...</p>
            </div>
          )}

          {status === 'invalid' && (
            <div className="text-center py-8">
              <span className="material-symbols-outlined text-5xl text-red-500 mb-4">error</span>
              <h2 className="text-xl font-bold text-[#00255a] mb-2">Invite Link Invalid</h2>
              <p className="text-[#64748b] mb-6">
                This invite link is invalid or has already been used. Contact your administrator for a new invite.
              </p>
              <a href="https://wa.me/263775827371" className="text-[#00255a] font-semibold hover:underline">
                Contact IASC Admin
              </a>
            </div>
          )}

          {status === 'valid' && (
            <>
              <header className="mb-8">
                <h2 className="text-3xl font-bold text-[#00255a] mb-2">Welcome, {invitee.fullName?.split(' ')[0]}</h2>
                <p className="text-[#44474f]">
                  You've been invited to join IASC Consultancy as an agent. Set a password to activate your account.
                </p>
                <p className="text-sm text-[#64748b] mt-2">{invitee.email}</p>
              </header>

              {error && (
                <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#00255a]/80 block px-1">Create Password</label>
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[#44474f] text-xl">lock</span>
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="w-full bg-[#f2f4f6] border border-[#c4c6d1] rounded-lg py-3.5 pl-11 pr-4 focus:ring-2 focus:ring-[#00255a]/10 focus:border-[#00255a] outline-none transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#00255a]/80 block px-1">Confirm Password</label>
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[#44474f] text-xl">lock_person</span>
                    <input
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter password"
                      className="w-full bg-[#f2f4f6] border border-[#c4c6d1] rounded-lg py-3.5 pl-11 pr-4 focus:ring-2 focus:ring-[#00255a]/10 focus:border-[#00255a] outline-none transition-all"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-3 transition-transform hover:scale-[1.02] disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
                >
                  {submitting ? 'Activating Account...' : 'Activate Account'}
                  {!submitting && <span className="material-symbols-outlined">arrow_forward</span>}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}